import { toaster } from '@/components/ui/toaster';
import {
  billingSettingsQueryKey,
  fetchBillingSettings,
  readBillingSettings,
} from '@/hooks/queries/use-billing-settings-query';
import { billingContextQueryKey } from '@/hooks/queries/use-billing-context-query';
import api from '@/libs/axiosInstance';
import type {
  ApiResponse,
  OrganizationBillingSettingsData,
  OrganizationClosureResponseData,
} from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { BillingTargetChangedError } from './use-billing-action-scope';
import { useBillingSettingsScope } from './use-billing-settings-scope';

const closureSchema = z
  .object({
    organizationId: z.string().min(1),
    brandId: z.string().min(1),
    status: z.enum(['pending', 'closed']),
    closedAt: z.string().datetime({ offset: true }).nullable(),
  })
  .strict()
  .refine((value) => (value.status === 'closed') === !!value.closedAt);

export function useCloseBillingOrganization(snapshot?: OrganizationBillingSettingsData) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const { scope, assertCurrent } = useBillingSettingsScope();
  const key = billingSettingsQueryKey(scope);
  return useMutation<
    OrganizationClosureResponseData,
    AxiosError<ApiResponse> | Error,
    void,
    typeof scope
  >({
    mutationKey: [...key, 'close'],
    retry: false,
    onMutate: () => {
      assertCurrent(scope);
      return scope;
    },
    mutationFn: async (): Promise<OrganizationClosureResponseData> => {
      assertCurrent(scope);
      const known = readBillingSettings(snapshot);
      if (
        !known?.canCloseOrganization ||
        known.closedAt ||
        known.organizationId !== scope.organizationId ||
        known.brandId !== scope.brandId
      )
        throw new Error('No authorized organization closure');
      const fresh = await client.fetchQuery({
        queryKey: key,
        queryFn: ({ signal }) => fetchBillingSettings(scope, assertCurrent, signal),
        staleTime: 0,
        retry: false,
      });
      assertCurrent(scope);
      if (!fresh.canCloseOrganization || fresh.closedAt)
        throw new Error('Organization closure unavailable');
      const response = await api.post<ApiResponse<unknown>>(
        '/dashboard/billing/organization-closure',
        { expectedOrganizationId: scope.organizationId, expectedBrandId: scope.brandId }
      );
      assertCurrent(scope);
      const result = closureSchema.safeParse(response.data.data);
      if (
        !response.data.success ||
        !result.success ||
        result.data.organizationId !== scope.organizationId ||
        result.data.brandId !== scope.brandId
      )
        throw new Error('Invalid organization closure response');
      const confirmed = await client.fetchQuery({
        queryKey: key,
        queryFn: ({ signal }) => fetchBillingSettings(scope, assertCurrent, signal),
        staleTime: 0,
        retry: false,
      });
      assertCurrent(scope);
      if (confirmed.closedAt)
        return {
          organizationId: confirmed.organizationId,
          brandId: confirmed.brandId,
          status: 'closed',
          closedAt: confirmed.closedAt,
        };
      if (result.data.status === 'closed') throw new Error('Organization closure not confirmed');
      return result.data;
    },
    onSuccess: async (data, _request, target) => {
      if (!target) return;
      assertCurrent(target);
      if (data.status !== 'closed') return;
      await client.invalidateQueries({ queryKey: billingContextQueryKey(target), exact: true });
      assertCurrent(target);
    },
    onError: (error, _request, target) => {
      if (error instanceof BillingTargetChangedError || !target) return;
      try {
        assertCurrent(target);
      } catch {
        return;
      }
      toaster.error({ title: t('common.error'), description: t('billing.settings.closureError') });
    },
  });
}
